import * as Sentry from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { AvatarUpload } from '../components/ui/AvatarUpload';
import { BackupManager } from '../components/backup/BackupManager';
import { useSenderAddress } from '../hooks/useSenderAddress';
import { SUPPORTED_COUNTRIES } from '../types/shipping';
import type { PostNLProductCodes } from '../types/shipping';
import { Settings as SettingsIcon, User, Building, Palette, Globe, Save, Shield, Bell, Database, HardDrive, Camera, CreditCard, MapPin, Percent, Phone, Mail, Upload, Image as ImageIcon, Sun, Moon, Clock, Map, Truck, Package, TriangleAlert as AlertTriangle, ShoppingBag, Webhook, CircleCheck as CheckCircle, Circle as XCircle, Loader as Loader2, RefreshCw, Printer, UsersRound, Bug, Copy } from 'lucide-react';
import { WarehouseList } from '../components/warehouse/WarehouseList';
import { BrevoSettingsTab } from '../components/settings/BrevoSettingsTab';
import { PhomemoPrinterSettings } from '../components/settings/PhomemoPrinterSettings';
import { WebhookStatusDashboard } from '../components/shopify/WebhookStatusDashboard';
import { ShopifyLocationMapping } from '../components/shopify/ShopifyLocationMapping';
import { WorkersSettingsTab } from '../components/settings/WorkersSettingsTab';
import { getAdminId } from '../utils/getAdminId';

export const Settings: React.FC = () => {
  const { user, refreshUserData, companies } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const { senderAddress, saving: savingShipping, saveSenderAddress, isConfigured } = useSenderAddress();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  // Company details are edited on whichever company is active, not on the
  // signed-in admin's own row.
  const companyId = getAdminId(user);
  const isOwnCompany = !!companyId && companyId === user?.id;
  const activeCompanyName = companies.find(c => c.id === companyId)?.company_name;

  const [activeTab, setActiveTab] = useState(isAdmin ? 'profile' : 'appearance');
  const [saving, setSaving] = useState(false);
  const [savingProductCodes, setSavingProductCodes] = useState(false);
  const [userIcon, setUserIcon] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(user?.logo_url || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
    company_address: user?.company_address || '',
    company_phone: user?.company_phone || '',
    company_email: user?.company_email || '',
    company_tax_id: user?.company_tax_id || '',
    company_website: user?.company_website || '',
    legal_entity_name: user?.legal_entity_name || '',
    company_street: user?.company_street || '',
    company_house_number: user?.company_house_number || '',
    company_postal_code: user?.company_postal_code || '',
    company_city: user?.company_city || '',
    company_country: user?.company_country || 'NL',
    kvk_number: user?.kvk_number || '',
    vat_number: user?.vat_number || '',
    payment_terms_days: user?.payment_terms_days || 14,
    iban: user?.iban || '',
    bic: user?.bic || '',
    bank_name: user?.bank_name || '',
    warehouse_name: user?.warehouse_name || '',
    warehouse_contact_name: user?.warehouse_contact_name || '',
    warehouse_street: user?.warehouse_street || '',
    warehouse_house_number: user?.warehouse_house_number || '',
    warehouse_house_number_ext: user?.warehouse_house_number_ext || '',
    warehouse_postal_code: user?.warehouse_postal_code || '',
    warehouse_city: user?.warehouse_city || '',
    warehouse_country_code: user?.warehouse_country_code || 'NL',
    warehouse_phone: user?.warehouse_phone || '',
    warehouse_email: user?.warehouse_email || '',
  });
  // The company half of `user` is an overlay that can arrive or change after
  // this form is first seeded (company switch, profile refresh). Reseeding on
  // that signature keeps the form from saving one company's KVK, VAT and IBAN
  // onto another company's row, without clobbering in-progress edits.
  const companySignature = JSON.stringify([
    companyId,
    user?.company_name, user?.company_address, user?.company_phone, user?.company_email,
    user?.company_tax_id, user?.company_website, user?.legal_entity_name, user?.company_street,
    user?.company_house_number, user?.company_postal_code, user?.company_city,
    user?.company_country, user?.kvk_number, user?.vat_number, user?.payment_terms_days,
    user?.iban, user?.bic, user?.bank_name, user?.logo_url,
    user?.warehouse_name, user?.warehouse_contact_name, user?.warehouse_street,
    user?.warehouse_house_number, user?.warehouse_house_number_ext,
    user?.warehouse_postal_code, user?.warehouse_city, user?.warehouse_country_code,
    user?.warehouse_phone, user?.warehouse_email,
  ]);

  useEffect(() => {
    if (!user) return;
    setProfileData(prev => ({
      ...prev,
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      company_name: user.company_name || '',
      company_address: user.company_address || '',
      company_phone: user.company_phone || '',
      company_email: user.company_email || '',
      company_tax_id: user.company_tax_id || '',
      company_website: user.company_website || '',
      legal_entity_name: user.legal_entity_name || '',
      company_street: user.company_street || '',
      company_house_number: user.company_house_number || '',
      company_postal_code: user.company_postal_code || '',
      company_city: user.company_city || '',
      company_country: user.company_country || 'NL',
      kvk_number: user.kvk_number || '',
      vat_number: user.vat_number || '',
      payment_terms_days: user.payment_terms_days || 14,
      iban: user.iban || '',
      bic: user.bic || '',
      bank_name: user.bank_name || '',
      warehouse_name: user.warehouse_name || '',
      warehouse_contact_name: user.warehouse_contact_name || '',
      warehouse_street: user.warehouse_street || '',
      warehouse_house_number: user.warehouse_house_number || '',
      warehouse_house_number_ext: user.warehouse_house_number_ext || '',
      warehouse_postal_code: user.warehouse_postal_code || '',
      warehouse_city: user.warehouse_city || '',
      warehouse_country_code: user.warehouse_country_code || 'NL',
      warehouse_phone: user.warehouse_phone || '',
      warehouse_email: user.warehouse_email || '',
    }));
    setLogoUrl(user.logo_url || null);
  }, [companySignature]);

  const [shippingData, setShippingData] = useState<{
    street: string;
    houseNumber: string;
    houseNumberExt: string;
    postalCode: string;
    city: string;
    countryCode: string;
    contactName: string;
    insurancePerItem: number;
    insuranceSdrRate: number;
    shipmentType: 'Gift' | 'Documents' | 'Commercial Goods' | 'Commercial Sample' | 'Returned Goods';
    defaultPackageType: 'mailbox' | 'package';
    globalpackInsurance: boolean;
    valueSplit: 1 | 2 | 3 | 4 | 5;
    openaiApiKey: string;
    aiAddressEnabled: boolean;
    postnlProductCodes: PostNLProductCodes;
  }>({
    street: '',
    houseNumber: '',
    houseNumberExt: '',
    postalCode: '',
    city: '',
    countryCode: 'NL',
    contactName: '',
    insurancePerItem: 5,
    insuranceSdrRate: 1,
    shipmentType: 'Commercial Goods',
    defaultPackageType: 'package',
    globalpackInsurance: false,
    valueSplit: 1,
    openaiApiKey: '',
    aiAddressEnabled: false,
    postnlProductCodes: {},
  });
  const [registeringWebhooks, setRegisteringWebhooks] = useState(false);
  const [webhookResult, setWebhookResult] = useState<{
    success: boolean;
    message: string;
    results?: Array<{
      shop: string;
      result: {
        success: boolean;
        registered: string[];
        failed: string[];
        errors: string[];
      };
    }>;
  } | null>(null);

  useEffect(() => {
    if (senderAddress) {
      setShippingData({
        street: senderAddress.street || '',
        houseNumber: senderAddress.houseNumber || '',
        houseNumberExt: senderAddress.houseNumberExt || '',
        postalCode: senderAddress.postalCode || '',
        city: senderAddress.city || '',
        countryCode: senderAddress.countryCode || 'NL',
        contactName: senderAddress.contactName || '',
        insurancePerItem: senderAddress.insurancePerItem ?? 5,
        insuranceSdrRate: senderAddress.insuranceSdrRate ?? 1,
        shipmentType: senderAddress.shipmentType ?? 'Commercial Goods',
        defaultPackageType: senderAddress.defaultPackageType ?? 'package',
        globalpackInsurance: senderAddress.globalpackInsurance ?? false,
        valueSplit: (senderAddress.valueSplit != null && senderAddress.valueSplit >= 1 && senderAddress.valueSplit <= 5)
          ? senderAddress.valueSplit
          : 1,
        openaiApiKey: senderAddress.openaiApiKey || '',
        aiAddressEnabled: senderAddress.aiAddressEnabled ?? false,
        postnlProductCodes: senderAddress.postnlProductCodes || {},
      });
    }
  }, [senderAddress]);

  useEffect(() => {
    const loadIcon = async () => {
      if (user?.id) {
        try {
          const { data: settings } = await supabase
            .from('user_settings')
            .select('avatar_data')
            .eq('user_id', user.id)
            .maybeSingle();

          if (settings?.avatar_data) {
            setUserIcon(settings.avatar_data);
          }
        } catch (error) {
          console.error('Failed to load avatar:', error);
        }
      }
    };
    loadIcon();
  }, [user?.id]);

  // Prevent non-admins from accessing profile tab
  useEffect(() => {
    if (activeTab === 'profile' && !isAdmin) {
      setActiveTab('appearance');
    }
  }, [activeTab, isAdmin]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file
    if (file.size > 40 * 1024 * 1024) {
      alert(t('settings.logoSizeError'));
      return;
    }

    if (!file.type.startsWith('image/png')) {
      alert(t('settings.logoTypeError'));
      return;
    }

    try {
      setUploadingLogo(true);
      if (!companyId) throw new Error('No active company selected');
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}/logo.${fileExt}`;

      // Upload to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // The logo is company letterhead, so it belongs to the active company.
      const { error: updateError } = await supabase
        .from('users')
        .update({ logo_url: publicUrl })
        .eq('id', companyId);

      if (updateError) throw updateError;

      setLogoUrl(publicUrl);
      alert(t('settings.logoSuccess'));
      await refreshUserData();
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(t('settings.logoFailed') + ': ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const structuredAddr = [
        profileData.company_street ? `${profileData.company_street}${profileData.company_house_number ? ' ' + profileData.company_house_number : ''}` : '',
        `${profileData.company_postal_code} ${profileData.company_city}`.trim(),
        profileData.company_country && profileData.company_country !== 'NL' ? profileData.company_country : '',
      ].filter(Boolean).join(', ');

      if (!companyId) throw new Error('No active company selected');

      const companyUpdates = {
        company_name: profileData.company_name,
        company_address: structuredAddr || profileData.company_address,
        company_phone: profileData.company_phone,
        company_email: profileData.company_email,
        company_tax_id: profileData.company_tax_id,
        company_website: profileData.company_website,
        legal_entity_name: profileData.legal_entity_name || null,
        company_street: profileData.company_street || null,
        company_house_number: profileData.company_house_number || null,
        company_postal_code: profileData.company_postal_code || null,
        company_city: profileData.company_city || null,
        company_country: profileData.company_country || 'NL',
        kvk_number: profileData.kvk_number || null,
        vat_number: profileData.vat_number || null,
        payment_terms_days: profileData.payment_terms_days || 14,
        iban: profileData.iban || null,
        bic: profileData.bic || null,
        bank_name: profileData.bank_name || null,
        warehouse_name: profileData.warehouse_name || null,
        warehouse_contact_name: profileData.warehouse_contact_name || null,
        warehouse_street: profileData.warehouse_street || null,
        warehouse_house_number: profileData.warehouse_house_number || null,
        warehouse_house_number_ext: profileData.warehouse_house_number_ext || null,
        warehouse_postal_code: profileData.warehouse_postal_code || null,
        warehouse_city: profileData.warehouse_city || null,
        warehouse_country_code: profileData.warehouse_country_code || 'NL',
        warehouse_phone: profileData.warehouse_phone || null,
        warehouse_email: profileData.warehouse_email || null,
      };

      // full_name belongs to the person who owns that row, so it is only
      // editable on your own company — never on one you are acting as.
      const { error } = await supabase
        .from('users')
        .update(
          isOwnCompany
            ? { full_name: profileData.full_name, ...companyUpdates }
            : companyUpdates,
        )
        .eq('id', companyId);

      if (error) throw error;

      alert(t('settings.profileSuccess'));
      await refreshUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(t('settings.profileFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpdate = (iconUrl: string | null) => {
    setUserIcon(iconUrl);
  };

  const handleSaveShipping = async (e: React.FormEvent) => {
    e.preventDefault();
    const insurance = Number(shippingData.insurancePerItem);
    if (!Number.isFinite(insurance) || insurance < 0 || insurance > 10000) {
      alert('Insurance per item must be between 0 and 10000 (€).');
      return;
    }
    const rawSdr = Number(shippingData.insuranceSdrRate);
    const insuranceSdrRate = Number.isFinite(rawSdr) && rawSdr > 0 ? rawSdr : 1;
    const success = await saveSenderAddress({
      ...shippingData,
      insurancePerItem: insurance,
      insuranceSdrRate,
      shipmentType: shippingData.shipmentType,
      globalpackInsurance: shippingData.globalpackInsurance,
      valueSplit: shippingData.valueSplit,
      openaiApiKey: shippingData.openaiApiKey,
      aiAddressEnabled: shippingData.aiAddressEnabled,
      postnlProductCodes: shippingData.postnlProductCodes,
    });
    if (success) {
      alert('Shipping address saved successfully');
    } else {
      alert('Failed to save shipping address');
    }
  };

  const handleSaveProductCodes = async () => {
    setSavingProductCodes(true);
    const success = await saveSenderAddress({
      ...shippingData,
      postnlProductCodes: shippingData.postnlProductCodes,
    });
    setSavingProductCodes(false);
    if (success) {
      alert('Shipment sending codes saved successfully');
    } else {
      alert('Failed to save shipment sending codes');
    }
  };

  const validatePostalCode = (code: string): boolean => {
    const dutchPattern = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
    return dutchPattern.test(code);
  };

  const handleRegisterWebhooks = async () => {
    setRegisteringWebhooks(true);
    setWebhookResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setWebhookResult({
          success: false,
          message: 'Authentication required. Please log in again.',
        });
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth/register-webhooks`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setWebhookResult({
          success: false,
          message: data.error || 'Failed to register webhooks',
        });
        return;
      }

      setWebhookResult(data);
    } catch (error) {
      setWebhookResult({
        success: false,
        message: error instanceof Error ? error.message : 'An error occurred',
      });
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  const allTabs = [
    { id: 'profile', label: t('settings.profile'), icon: User, adminOnly: true },
    { id: 'shipping', label: 'Shipping Address', icon: Truck, adminOnly: true },
    { id: 'shopify', label: 'Shopify', icon: ShoppingBag, adminOnly: true },
    { id: 'warehouse', label: 'Warehouse Plan', icon: Map, adminOnly: true },
    { id: 'brevo', label: 'Brevo Email', icon: Mail, adminOnly: true },
    { id: 'workers', label: 'Workers', icon: UsersRound, adminOnly: true },
    { id: 'printers', label: 'Printers', icon: Printer },
    { id: 'appearance', label: t('settings.appearance'), icon: Palette },
    { id: 'backup', label: t('settings.backup'), icon: HardDrive },
    { id: 'contact', label: 'Contact Us', icon: Mail }
  ];

  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('settings.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('settings.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Profile & Company Tab - Admin Only */}
          {activeTab === 'profile' && isAdmin && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                {t('settings.profileTitle')}
              </h2>

              {!isOwnCompany && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                  <Building className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    You are editing the company details of{' '}
                    <span className="font-semibold">{activeCompanyName || 'another company'}</span>.
                    These details appear on that company's invoices and receipts.
                  </p>
                </div>
              )}

              <form onSubmit={handleSaveProfile} className="space-y-6">
                {/* Company Logo Upload */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <ImageIcon className="h-5 w-5 mr-2" />
                    {t('settings.companyLogo')}
                  </h3>
                  <div className="flex flex-col items-center space-y-4">
                    {logoUrl ? (
                      <div className="relative">
                        <img
                          src={logoUrl}
                          alt="Company Logo"
                          crossOrigin="anonymous"
                          className="w-32 h-32 object-contain border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-800"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                        <ImageIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <label className="btn btn-secondary cursor-pointer">
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingLogo ? t('settings.uploading') : t('settings.uploadLogo')}
                        <input
                          type="file"
                          accept="image/png"
                          onChange={handleLogoUpload}
                          className="hidden"
                          disabled={uploadingLogo}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('settings.logoFormat')}
                    </p>
                  </div>
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    disabled={!isOwnCompany}
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    value={profileData.email}
                    placeholder="Enter email"
                    disabled
                  />
                </div>

                {/* Company & Brand */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Company & Brand
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Company / Trade Name"
                      value={profileData.company_name}
                      onChange={(e) => setProfileData({ ...profileData, company_name: e.target.value })}
                      placeholder="Your Brand Name"
                    />
                    <Input
                      label="Legal Entity Name"
                      value={profileData.legal_entity_name}
                      onChange={(e) => setProfileData({ ...profileData, legal_entity_name: e.target.value })}
                      placeholder="Company B.V."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Street"
                        value={profileData.company_street}
                        onChange={(e) => setProfileData({ ...profileData, company_street: e.target.value })}
                        placeholder="Keizersgracht"
                      />
                    </div>
                    <Input
                      label="House Number"
                      value={profileData.company_house_number}
                      onChange={(e) => setProfileData({ ...profileData, company_house_number: e.target.value })}
                      placeholder="123"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Postal Code"
                      value={profileData.company_postal_code}
                      onChange={(e) => setProfileData({ ...profileData, company_postal_code: e.target.value })}
                      placeholder="1015 AB"
                    />
                    <Input
                      label="City"
                      value={profileData.company_city}
                      onChange={(e) => setProfileData({ ...profileData, company_city: e.target.value })}
                      placeholder="Amsterdam"
                    />
                    <Input
                      label="Country"
                      value={profileData.company_country}
                      onChange={(e) => setProfileData({ ...profileData, company_country: e.target.value })}
                      placeholder="NL"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Phone Number"
                      value={profileData.company_phone}
                      onChange={(e) => setProfileData({ ...profileData, company_phone: e.target.value })}
                      placeholder="+31 20 123 4567"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={profileData.company_email}
                      onChange={(e) => setProfileData({ ...profileData, company_email: e.target.value })}
                      placeholder="info@company.com"
                    />
                  </div>

                  <Input
                    label="Website"
                    value={profileData.company_website}
                    onChange={(e) => setProfileData({ ...profileData, company_website: e.target.value })}
                    placeholder="https://www.company.com"
                  />
                </div>

                {/* Warehouse / Returns Address */}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                        <Package className="h-5 w-5 mr-2" />
                        Warehouse / Returns Address
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Where return labels ship back to. Separate from your invoice address and your PostNL sender address.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setProfileData({
                        ...profileData,
                        warehouse_street: profileData.company_street,
                        warehouse_house_number: profileData.company_house_number,
                        warehouse_postal_code: profileData.company_postal_code,
                        warehouse_city: profileData.company_city,
                        warehouse_country_code: profileData.company_country || 'NL',
                        warehouse_phone: profileData.company_phone,
                        warehouse_email: profileData.company_email,
                      })}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy from company address
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Warehouse Name"
                      value={profileData.warehouse_name}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_name: e.target.value })}
                      placeholder="Main Warehouse"
                    />
                    <Input
                      label="Contact Name"
                      value={profileData.warehouse_contact_name}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_contact_name: e.target.value })}
                      placeholder="Returns Department"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Street"
                        value={profileData.warehouse_street}
                        onChange={(e) => setProfileData({ ...profileData, warehouse_street: e.target.value })}
                        placeholder="Keizersgracht"
                      />
                    </div>
                    <Input
                      label="House Number"
                      value={profileData.warehouse_house_number}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_house_number: e.target.value })}
                      placeholder="123"
                    />
                    <Input
                      label="Extension"
                      value={profileData.warehouse_house_number_ext}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_house_number_ext: e.target.value })}
                      placeholder="A"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Postal Code"
                      value={profileData.warehouse_postal_code}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_postal_code: e.target.value })}
                      placeholder="1015 AB"
                    />
                    <Input
                      label="City"
                      value={profileData.warehouse_city}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_city: e.target.value })}
                      placeholder="Amsterdam"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <select
                        value={profileData.warehouse_country_code}
                        onChange={(e) => setProfileData({ ...profileData, warehouse_country_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        {SUPPORTED_COUNTRIES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Phone Number"
                      value={profileData.warehouse_phone}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_phone: e.target.value })}
                      placeholder="+31 20 123 4567"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={profileData.warehouse_email}
                      onChange={(e) => setProfileData({ ...profileData, warehouse_email: e.target.value })}
                      placeholder="returns@company.com"
                    />
                  </div>
                </div>

                {/* Tax & Registration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Tax & Registration
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="KvK Number"
                      value={profileData.kvk_number}
                      onChange={(e) => setProfileData({ ...profileData, kvk_number: e.target.value })}
                      placeholder="12345678"
                    />
                    <Input
                      label="VAT Number (BTW)"
                      value={profileData.vat_number}
                      onChange={(e) => setProfileData({ ...profileData, vat_number: e.target.value })}
                      placeholder="NL123456789B01"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Tax ID (non-NL)"
                      value={profileData.company_tax_id}
                      onChange={(e) => setProfileData({ ...profileData, company_tax_id: e.target.value })}
                      placeholder="Optional for non-NL entities"
                    />
                    <Input
                      label="Payment Terms (days)"
                      type="number"
                      value={String(profileData.payment_terms_days)}
                      onChange={(e) => setProfileData({ ...profileData, payment_terms_days: parseInt(e.target.value) || 14 })}
                      placeholder="14"
                    />
                  </div>
                </div>

                {/* Banking */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Banking Details (For Invoices)
                  </h3>

                  <Input
                    label="IBAN"
                    value={profileData.iban}
                    onChange={(e) => setProfileData({ ...profileData, iban: e.target.value })}
                    placeholder="NL91ABNA0417164300"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="BIC / SWIFT"
                      value={profileData.bic}
                      onChange={(e) => setProfileData({ ...profileData, bic: e.target.value })}
                      placeholder="ABNANL2A"
                    />
                    <Input
                      label="Bank Name"
                      value={profileData.bank_name}
                      onChange={(e) => setProfileData({ ...profileData, bank_name: e.target.value })}
                      placeholder="ABN AMRO"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" variant="primary" loading={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Profile
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Access Denied Message for Profile */}
          {activeTab === 'profile' && !isAdmin && (
            <div className="card p-8">
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Access Denied
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Only administrators can access Profile & Company Information settings.
                </p>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800 max-w-md mx-auto">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Your role: <span className="font-semibold capitalize">{user?.role || 'Unknown'}</span>
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                    Contact your administrator if you need to update company information.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Shipping Address Tab - Admin Only */}
          {activeTab === 'shipping' && isAdmin && (
            <div className="card p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Shipping Sender Address
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Configure your default sender address for PostNL shipping labels. This address will be used as the return address on all labels you create.
                </p>
              </div>

              {!isConfigured && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 dark:text-amber-300">
                      Shipping address not configured
                    </h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      You need to configure your sender address before you can create shipping labels.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSaveShipping} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                    <Package className="h-5 w-5 mr-2" />
                    Sender Address Details
                  </h3>

                  <Input
                    label="Contact Name"
                    value={shippingData.contactName}
                    onChange={(e) => setShippingData({ ...shippingData, contactName: e.target.value })}
                    placeholder="Company name or contact person"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Input
                        label="Street Name"
                        value={shippingData.street}
                        onChange={(e) => setShippingData({ ...shippingData, street: e.target.value })}
                        placeholder="Hoofdstraat"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="House No."
                        value={shippingData.houseNumber}
                        onChange={(e) => setShippingData({ ...shippingData, houseNumber: e.target.value })}
                        placeholder="123"
                        required
                      />
                      <Input
                        label="Ext."
                        value={shippingData.houseNumberExt}
                        onChange={(e) => setShippingData({ ...shippingData, houseNumberExt: e.target.value })}
                        placeholder="A"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Postal Code"
                      value={shippingData.postalCode}
                      onChange={(e) => setShippingData({ ...shippingData, postalCode: e.target.value.toUpperCase() })}
                      placeholder="1234 AB"
                      required
                    />
                    <Input
                      label="City"
                      value={shippingData.city}
                      onChange={(e) => setShippingData({ ...shippingData, city: e.target.value })}
                      placeholder="Amsterdam"
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <select
                        value={shippingData.countryCode}
                        onChange={(e) => setShippingData({ ...shippingData, countryCode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        required
                      >
                        {SUPPORTED_COUNTRIES.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mt-4 flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="globalpack-insurance"
                        checked={shippingData.globalpackInsurance}
                        onChange={(e) => setShippingData({ ...shippingData, globalpackInsurance: e.target.checked })}
                        className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500"
                      />
                      <div>
                        <label htmlFor="globalpack-insurance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable GlobalPack Insurance (non-EU)
                        </label>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          When enabled, non-EU shipments use insured GlobalPack (4947) with the Insurance per item value below.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Shipping options</h4>
                    <Input
                      type="number"
                      label="Insurance per item (€)"
                      value={shippingData.insurancePerItem ?? 5}
                      onChange={(e) => {
                        const v = e.target.value;
                        const num = v === '' ? 5 : parseFloat(v);
                        setShippingData({ ...shippingData, insurancePerItem: Number.isFinite(num) ? num : 5 });
                      }}
                      placeholder="5"
                      min={0}
                      max={10000}
                      step={0.01}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Used as declared insured value per item on PostNL labels.
                    </p>
                    <Input
                      type="number"
                      label="SDR rate (1 EUR = X SDR)"
                      value={shippingData.insuranceSdrRate ?? 1}
                      onChange={(e) => {
                        const v = e.target.value;
                        const num = v === '' ? 1 : parseFloat(v);
                        setShippingData({ ...shippingData, insuranceSdrRate: Number.isFinite(num) && num > 0 ? num : 1 });
                      }}
                      placeholder="1"
                      min={0.01}
                      max={100}
                      step={0.01}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Manual rate for non-EU insured value (PostNL uses XDR).
                    </p>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Item Category (for customs)
                      </label>
                      <select
                        value={shippingData.shipmentType}
                        onChange={(e) => setShippingData({ ...shippingData, shipmentType: e.target.value as typeof shippingData.shipmentType })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        <option value="Commercial Goods">Commercial Goods (default)</option>
                        <option value="Gift">Gift</option>
                        <option value="Documents">Documents</option>
                        <option value="Commercial Sample">Commercial Sample</option>
                        <option value="Returned Goods">Returned Goods</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Used as the shipment type on PostNL customs declarations for non-EU destinations.
                      </p>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Default pack type
                      </label>
                      <select
                        value={shippingData.defaultPackageType}
                        onChange={(e) => setShippingData({ ...shippingData, defaultPackageType: e.target.value as 'mailbox' | 'package' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        <option value="package">Package (pakket)</option>
                        <option value="mailbox">Mailbox (brievenbuspakje)</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Determines the PostNL product code: NL (3085/2928), BE/EU/Non-EU (6550/6942).
                      </p>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Value split (non-EU labels)
                      </label>
                      <select
                        value={shippingData.valueSplit}
                        onChange={(e) => setShippingData({ ...shippingData, valueSplit: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        <option value={1}>1=1 (full value)</option>
                        <option value={2}>1=1/2</option>
                        <option value={3}>1=1/3</option>
                        <option value={4}>1=1/4</option>
                        <option value={5}>1=1/5</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Divide the declared value printed on non-EU labels (e.g. 24.99 with 1/2 becomes 12.49).
                      </p>
                    </div>

                  </div>
                </div>

                {isConfigured && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">
                      Current Sender Address
                    </h4>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      {senderAddress?.contactName && `${senderAddress.contactName}, `}
                      {senderAddress?.street} {senderAddress?.houseNumber}
                      {senderAddress?.houseNumberExt && ` ${senderAddress.houseNumberExt}`}, {senderAddress?.postalCode} {senderAddress?.city}, {senderAddress?.countryCode}
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L10 6.036 6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.789l1.599.799L9 4.323V3a1 1 0 011-1z" /></svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">AI Address Assistant</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Powered by OpenAI GPT-4o-mini — auto-detect, transliterate &amp; fix addresses</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={shippingData.aiAddressEnabled}
                      onClick={() => setShippingData({ ...shippingData, aiAddressEnabled: !shippingData.aiAddressEnabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${shippingData.aiAddressEnabled ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${shippingData.aiAddressEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {shippingData.aiAddressEnabled && (
                    <div className="space-y-3 pl-13">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OpenAI API Key</label>
                        <div className="relative">
                          <input
                            type="password"
                            value={shippingData.openaiApiKey}
                            onChange={(e) => setShippingData({ ...shippingData, openaiApiKey: e.target.value })}
                            placeholder={senderAddress?.aiAddressEnabled ? "Key is set — enter a new key to replace" : "sk-..."}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono text-sm"
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                            <Shield className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Get a key at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">OpenAI Platform</a>. Uses GPT-4o-mini (~$0.15 per 1M tokens). The key is stored securely in your account.
                        </p>
                      </div>
                      <div className="p-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                        <p className="text-sm text-violet-700 dark:text-violet-300">
                          When enabled, the AI will automatically process addresses fetched from Shopify across all label generation pages — detecting non-Latin characters, fixing formatting, and reporting issues.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button type="submit" variant="primary" loading={savingShipping}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Shipping Address
                  </Button>
                </div>
              </form>

              <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
                      <Package className="h-5 w-5 mr-2 text-cyan-600" />
                      Shipment Sending Codes
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Override PostNL product codes per service &amp; destination. Leave empty to use defaults (shown as placeholders).
                      NL: 3085/2928. BE/EU/Non-EU: Pakje T&amp;T (6550) / Buspakje T&amp;T (6942) based on Default pack type.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShippingData({ ...shippingData, postnlProductCodes: {} })}
                    className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline"
                  >
                    Reset to defaults
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400">
                        <th className="text-left py-1.5 pr-2 font-medium">Service</th>
                        <th className="text-center py-1.5 px-1 font-medium">NL</th>
                        <th className="text-center py-1.5 px-1 font-medium">BE</th>
                        <th className="text-center py-1.5 px-1 font-medium">EU (Tracked)</th>
                        <th className="text-center py-1.5 px-1 font-medium">Non-EU</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {([
                        { label: 'Pakje (standard)', keys: ['package_nl', 'package_be', 'package_eu', 'package_non_eu'], defaults: ['3085', '6550', '6550', '6550'] },
                        { label: 'Buspakje (mailbox)', keys: ['mailbox_nl', 'mailbox_be', 'mailbox_eu', 'mailbox_non_eu'], defaults: ['2928', '6942', '6942', '6942'] },
                        { label: 'Insured', keys: ['insured_nl', 'insured_be', 'insured_eu', 'insured_non_eu'], defaults: ['3087', '4952', '4952', '4947'] },
                        { label: 'Signature', keys: ['signature_nl', 'signature_be', 'signature_eu', 'signature_non_eu'], defaults: ['3390', '4952', '4952', '4909'] },
                        { label: 'Evening', keys: ['evening_nl', null, null, null], defaults: ['3089', '', '', ''] },
                        { label: 'Sunday', keys: ['sunday_nl', null, null, null], defaults: ['3385', '', '', ''] },
                      ] as const).map((row) => (
                        <tr key={row.label}>
                          <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{row.label}</td>
                          {row.keys.map((key, i) => (
                            <td key={key ?? `empty-${i}`} className="py-1.5 px-1">
                              {key ? (
                                <input
                                  type="text"
                                  value={(shippingData.postnlProductCodes as Record<string, string | undefined>)[key] || ''}
                                  onChange={(e) => {
                                    const val = e.target.value.trim();
                                    setShippingData({
                                      ...shippingData,
                                      postnlProductCodes: { ...shippingData.postnlProductCodes, [key]: val || undefined },
                                    });
                                  }}
                                  placeholder={row.defaults[i]}
                                  className="w-full px-2 py-1 text-center text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-cyan-500 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                  maxLength={6}
                                />
                              ) : (
                                <span className="block text-center text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  NL: Pakje 3085, Brievenbuspakje 2928. BE/EU/Non-EU: Pakje T&amp;T 6550, Buspakje T&amp;T 6942. Applied based on Default pack type. Insured non-EU uses 4947 (GlobalPack).
                </p>
                <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="primary" loading={savingProductCodes} onClick={handleSaveProductCodes}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Sending Codes
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Shopify Tab - Admin Only */}
          {activeTab === 'shopify' && isAdmin && (
            <div className="space-y-6">
              <div className="card p-6">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Shopify Integration Settings
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Monitor webhook health, test connections, and manage your Shopify integration.
                  </p>
                </div>

                <WebhookStatusDashboard />
              </div>

              {/* Manual Webhook Registration Section */}
              <div className="card p-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Webhook className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Re-register All Webhooks
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Force re-register all webhooks with Shopify. Use this if some webhooks are missing
                        or if you need to update the webhook endpoints after configuration changes.
                      </p>
                      <Button
                        onClick={handleRegisterWebhooks}
                        disabled={registeringWebhooks}
                        variant="primary"
                      >
                        {registeringWebhooks ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Registering...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Re-register All Webhooks
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {webhookResult && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    webhookResult.success
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}>
                    <div className="flex items-start gap-3">
                      {webhookResult.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h4 className={`font-medium ${
                          webhookResult.success
                            ? 'text-green-800 dark:text-green-300'
                            : 'text-red-800 dark:text-red-300'
                        }`}>
                          {webhookResult.message}
                        </h4>
                        {webhookResult.results && webhookResult.results.length > 0 && (
                          <div className="mt-3 space-y-3">
                            {webhookResult.results.map((shopResult, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {shopResult.shop}
                                </p>
                                {shopResult.result.registered.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-xs text-green-700 dark:text-green-400 mb-1">
                                      Registered ({shopResult.result.registered.length}):
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {shopResult.result.registered.map((topic) => (
                                        <span
                                          key={topic}
                                          className="px-2 py-0.5 text-xs font-mono bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded"
                                        >
                                          {topic}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {shopResult.result.failed.length > 0 && (
                                  <div>
                                    <p className="text-xs text-red-700 dark:text-red-400 mb-1">
                                      Failed ({shopResult.result.failed.length}):
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {shopResult.result.failed.map((topic) => (
                                        <span
                                          key={topic}
                                          className="px-2 py-0.5 text-xs font-mono bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded"
                                        >
                                          {topic}
                                        </span>
                                      ))}
                                    </div>
                                    {shopResult.result.errors.length > 0 && (
                                      <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400 font-mono overflow-auto max-h-24">
                                        {shopResult.result.errors.map((err, errIdx) => (
                                          <div key={errIdx}>{err}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800 dark:text-amber-300">
                        When to use manual registration
                      </h4>
                      <ul className="text-sm text-amber-700 dark:text-amber-400 mt-2 space-y-1 list-disc list-inside">
                        <li>If orders or inventory changes are not syncing automatically</li>
                        <li>After reconnecting your Shopify store</li>
                        <li>If you see webhook-related errors in your logs</li>
                        <li>When setting up a new integration environment</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shopify Location Mapping Section */}
              <div className="card p-6">
                <ShopifyLocationMapping />
              </div>
            </div>
          )}

          {/* Printers Tab */}
          {activeTab === 'printers' && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                Printers
              </h2>
              <PhomemoPrinterSettings />
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="card p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
                Appearance & Language
              </h2>

              <div className="space-y-10">
                {/* Theme Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-5 flex items-center">
                    <Palette className="h-5 w-5 mr-2 text-blue-500" />
                    Theme Preference
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => isDark && toggleTheme()}
                      className={`relative p-6 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        !isDark
                          ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 shadow-lg shadow-cyan-500/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-white to-gray-100 border-2 border-gray-300 rounded-lg mr-4 flex items-center justify-center">
                          <Sun className="h-6 w-6 text-yellow-500" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-lg text-gray-900 dark:text-gray-100">Light Mode</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Bright and clear</div>
                        </div>
                      </div>
                      {!isDark && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>

                    <button
                      onClick={() => !isDark && toggleTheme()}
                      className={`relative p-6 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        isDark
                          ? 'border-cyan-500 bg-gradient-to-br from-gray-800 to-gray-900 shadow-lg shadow-cyan-500/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-950 border-2 border-gray-700 rounded-lg mr-4 flex items-center justify-center">
                          <Moon className="h-6 w-6 text-blue-400" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-lg text-gray-900 dark:text-gray-100">Dark Mode</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Easy on the eyes</div>
                        </div>
                      </div>
                      {isDark && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* Language Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-5 flex items-center">
                    <Globe className="h-5 w-5 mr-2 text-blue-500" />
                    Language & Regional Settings
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Choose your preferred language for the interface and invoices
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { code: 'en', flag: '🇺🇸', name: 'English', desc: 'English invoices' },
                      { code: 'fr', flag: '🇫🇷', name: 'Français', desc: 'Factures en français' },
                      { code: 'es', flag: '🇪🇸', name: 'Español', desc: 'Facturas en español' },
                      { code: 'nl', flag: '🇳🇱', name: 'Nederlands', desc: 'Nederlandse facturen' }
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage(lang.code as any)}
                        className={`relative p-5 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                          language === lang.code
                            ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 shadow-lg shadow-cyan-500/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-5xl mb-3">{lang.flag}</div>
                          <div className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-1">{lang.name}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{lang.desc}</div>
                        </div>
                        {language === lang.code && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Backup Tab */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <div className="card p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
                  Backup & Data Management
                </h2>
                <BackupManager />
              </div>

              <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                  <Bug className="h-5 w-5 mr-2 text-red-500" />
                  Diagnostics
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Send a test error to Sentry to verify that error tracking is working correctly.
                </p>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const eventId = Sentry.captureException(
                      new Error('Sentry test error from Settings')
                    );
                    await Sentry.flush(5000);
                    alert(`Test event queued.\nEvent ID: ${eventId}\n\nCheck the Sentry dashboard to confirm delivery.`);
                  }}
                >
                  <Bug className="h-4 w-4 mr-2" />
                  Send Test Error
                </Button>
              </div>
            </div>
          )}

          {/* Warehouse Plan Tab */}
          {activeTab === 'brevo' && isAdmin && (
            <BrevoSettingsTab />
          )}

          {activeTab === 'workers' && isAdmin && (
            <WorkersSettingsTab />
          )}

          {activeTab === 'warehouse' && isAdmin && (
            <div className="card p-6">
              <WarehouseList />
            </div>
          )}

          {/* Contact Us Tab */}
          {activeTab === 'contact' && (
            <div className="card p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Contact Us
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Need help? We're here for you. Get in touch with our support team.
                </p>
              </div>

              <div className="space-y-6">
                {/* Email Support */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Email Support
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Send us an email and we'll respond within 24 hours
                      </p>
                      <a
                        href="mailto:support@getinv.app"
                        className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium hover:underline"
                      >
                        support@getinv.app
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Website */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-6 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Globe className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Official Website
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Visit our website for more information and updates
                      </p>
                      <a
                        href="https://www.getinv.app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium hover:underline"
                      >
                        www.getinv.app
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Support Hours */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl border border-green-200 dark:border-green-800">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Support Hours
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Our support team is available to help you
                      </p>
                      <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <div className="font-bold text-lg text-gray-900 dark:text-gray-100">
                            24/7 Support
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Available 24 hours a day, 7 days a week
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};